import styled, { css } from 'styled-components/macro';

import { ThemedCircleButton } from 'components/ThemedCircleButton';
import { ReactComponent as CameraSvg } from 'components/svg/Camera.svg';
import { large, medium } from 'styles/breakpoints';
import { error } from 'styles/typography';
import { IWithThemeProps } from 'typings';

interface IPreviewProps {
  isLoading: boolean;
}

export const FileBox = styled.button`
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  height: 136px;
  justify-content: center;
  overflow: hidden;
  padding: 0;
  position: relative;
  width: 136px;

  ${medium`
    height: 150px;
    width: 150px;
  `}

  &:not(:last-child) {
    margin: 0 0 12px 0;

    ${large`
      margin: 0;
    `}
  }
`;

export const UploadBox = styled(FileBox)`
  align-items: center;
  z-index: 10;

  ${({ theme }: IWithThemeProps) => `
    border-color: ${theme.mediaUploadActionItemColor};
    transition: background-color ${theme.transitionDurationPrimary} ease;

    [data-svg-class="AddIcon-stroke"] {
      stroke: ${theme.mediaUploadActionItemColor};
      transition: stroke ${theme.transitionDurationPrimary} ease;
    }

    &:hover {
      background-color: ${theme.mediaUploadActionItemColor};

      [data-svg-class="AddIcon-stroke"] {
        stroke: ${theme.bottomForegroundActionSecondary};
      }
    }
  `}
`;

const previewStyles = css`
  height: 100%;
  object-fit: cover;
  width: 100%;

  ${({ theme }: IWithThemeProps) => `
    transition: opacity ${theme.transitionDurationPrimary} ease;
  `}
`;

export const ImagePreview = styled.img<IPreviewProps>`
  ${previewStyles}

  ${({ isLoading }) => `
    opacity: ${isLoading ? 0.6 : 1};
  `}
`;

const CameraSvgSvgWithProps = ({
  isLoading: _isLoading,
  ...props
}: IPreviewProps) => {
  return <CameraSvg {...props} />;
};

export const VideoPreview = styled(CameraSvgSvgWithProps)<IPreviewProps>`
  ${previewStyles}

  padding: 25px;

  ${({ theme }: IWithThemeProps) => `
    background: ${theme.bottomForegroundPrimary};

    [data-svg-class="Icon-stroke"] {
      fill: ${theme.bottomForegroundSecondary};
      stroke: ${theme.bottomForegroundSecondary};
    }
  `}

  ${({ isLoading }) => `
    opacity: ${isLoading ? 0.6 : 1};
  `}
`;

export const Tooltip = styled.div`
  ${error}

  border-radius: 4px;
  display: none;
  padding: 8px 13px;
  position: absolute;
  z-index: 100;

  ${({ theme }: IWithThemeProps) => `
    background-color: ${theme.topBackgroundColor};
    border: 1px solid ${theme.topBackgroundColor};
    color: ${theme.topForegroundPrimary};
  `}
`;

export const TooltipBox = styled.div`
  position: relative;

  &:hover ${Tooltip} {
    display: block;
  }
`;

export const ProgressBarOuter = styled.div`
  border-radius: 5px;
  bottom: 6px;
  height: 10px;
  left: 5px;
  overflow: hidden;
  position: absolute;
  width: 93%;

  ${({ theme }: IWithThemeProps) => `
    background-color: ${theme.inputBorderColorDefault};
  `}
`;

export const ProgressBarInner = styled.div`
  height: 100%;

  ${({ theme }: IWithThemeProps) => `
    background-color: ${theme.mediaUploadActionItemColor};
  `}
`;

const buttonStyles = css`
  position: absolute;
  right: 3px;
  top: 3px;
  z-index: 100;
`;

export const CancelButton = styled(ThemedCircleButton)`
  ${buttonStyles}

  [data-svg-class="Close-circle"] {
    stroke: $media-upload-action-item-color;
    fill: $bottom-foreground-action-secondary;
  }

  [data-svg-class='Close-line'] {
    stroke: $media-upload-action-item-color;
  }

  &:hover {
    [data-svg-class='Close-circle'] {
      fill: $media-upload-action-item-color;
    }

    [data-svg-class='Close-line'] {
      stroke: $bottom-foreground-action-secondary;
    }
  }
`;

export const DeleteButton = styled(ThemedCircleButton)`
  ${buttonStyles}
`;
